import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RequestStatus } from '../../utils/Enums';
import { apiClient } from '../../utils/ApiClient';
import { Vendor } from '../../models/Vendor';
import {NewVendorFormProps} from "../../components/vendors/NewVendorScreen";

interface VendorsState {
  vendors: Vendor[];
  currentVendor: Vendor | null;
  getVendorsStatus: RequestStatus;
  createVendorStatus: RequestStatus;
  updateVendorStatus: RequestStatus;
  deleteVendorStatus: RequestStatus;
  getCurrentVendorStatus: RequestStatus;
  errors: string[];
  currentPage: number;
  totalPages: number;
  total: number;
}

const initialState: VendorsState = {
  vendors: [],
  currentVendor: null,
  getVendorsStatus: RequestStatus.Idle,
  createVendorStatus: RequestStatus.Idle,
  updateVendorStatus: RequestStatus.Idle,
  deleteVendorStatus: RequestStatus.Idle,
  getCurrentVendorStatus: RequestStatus.Idle,
  errors: [],
  currentPage: 1,
  totalPages: 1,
  total: 0
};

export const getVendors = createAsyncThunk(
    'clients/getClients',
    async (_, { rejectWithValue, getState }) => {
      const urlParams = new URLSearchParams(window.location.search);
      const page = urlParams.get('p') || 1;
      const search = urlParams.get('search') || '';

      const response = await apiClient.get(`vendors?page=${page}&search=${search}`);
      return response.data;
    }
);

export const createVendor = createAsyncThunk('vendors/createVendor', async (vendorData: NewVendorFormProps, { rejectWithValue }) => {
  const response = await apiClient.post('vendors', vendorData);
  return response.data;
});

export const updateVendor = createAsyncThunk(
  'vendors/updateVendor',
  async ({ id, vendorData }: { id: string | undefined; vendorData: any }, { rejectWithValue }) => {
    const response = await apiClient.put(`vendors/${id}`, vendorData);
    return response.data;
  }
);

export const deleteVendor = createAsyncThunk('vendors/deleteVendor', async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
  const response = await apiClient.delete(`vendors/${id}`);
  return response.data;
});

export const getCurrentVendor = createAsyncThunk(
  'vendors/getCurrentVendor',
  async ({ id }: { id: string | undefined }, { rejectWithValue }) => {
    const response = await apiClient.get(`vendors/${id}`);
    return response.data;
  }
);

const vendorsSlice = createSlice({
  name: 'vendors',
  initialState,
  reducers: {
    resetCreateVendorStatus: (state) => {
      state.createVendorStatus = RequestStatus.Idle;
      state.currentVendor = null;
    },
    resetUpdateVendorStatus: (state) => {
      state.updateVendorStatus = RequestStatus.Idle;
      state.currentVendor = null;
    },
    resetDeleteVendorStatus: (state) => {
      state.deleteVendorStatus = RequestStatus.Idle;
      state.currentVendor = null;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getVendors.pending, (state) => {
        state.getVendorsStatus = RequestStatus.Loading;
      })
      .addCase(getVendors.fulfilled, (state, action) => {
        state.getVendorsStatus = RequestStatus.Success;
        state.vendors = action.payload.data;
        state.totalPages = action.payload.last_page;
        state.total = action.payload.total;
      })
      .addCase(getVendors.rejected, (state) => {
        state.getVendorsStatus = RequestStatus.Failed;
      })
      .addCase(createVendor.pending, (state) => {
        state.createVendorStatus = RequestStatus.Loading;
        state.errors = [];
      })
      .addCase(createVendor.fulfilled, (state) => {
        state.createVendorStatus = RequestStatus.Success;
      })
      .addCase(createVendor.rejected, (state, action) => {
        state.createVendorStatus = RequestStatus.Failed;
        const errors = JSON.parse(action?.error?.message ?? '{}');
        if (errors) {
          state.errors = Array.isArray(errors.errors) ? errors.errors : [];
        } else {
          state.errors = ['Tuvastamata viga.'];
        }
      })
      .addCase(updateVendor.pending, (state) => {
        state.updateVendorStatus = RequestStatus.Loading;
        state.errors = [];
      })
      .addCase(updateVendor.fulfilled, (state) => {
        state.updateVendorStatus = RequestStatus.Success;
      })
      .addCase(updateVendor.rejected, (state, action) => {
        state.updateVendorStatus = RequestStatus.Failed;
        const errors = JSON.parse(action?.error?.message ?? '{}');
        if (errors) {
          state.errors = Array.isArray(errors.errors) ? errors.errors : [];
        } else {
          state.errors = ['Tuvastamata viga.'];
        }
      })
      .addCase(deleteVendor.pending, (state) => {
        state.deleteVendorStatus = RequestStatus.Loading;
      })
      .addCase(deleteVendor.fulfilled, (state) => {
        state.deleteVendorStatus = RequestStatus.Success;
      })
      .addCase(deleteVendor.rejected, (state) => {
        state.deleteVendorStatus = RequestStatus.Failed;
      })
      .addCase(getCurrentVendor.pending, (state) => {
        state.getCurrentVendorStatus = RequestStatus.Loading;
      })
      .addCase(getCurrentVendor.fulfilled, (state, action) => {
        const vendorData = action.payload;
        state.currentVendor = {
          ...vendorData,
        };
        state.getCurrentVendorStatus = RequestStatus.Success;
      })
      .addCase(getCurrentVendor.rejected, (state) => {
        state.getCurrentVendorStatus = RequestStatus.Failed;
      });
  },
});

export const {
  resetCreateVendorStatus,
  resetUpdateVendorStatus,
  resetDeleteVendorStatus,
} = vendorsSlice.actions;

export default vendorsSlice.reducer;